import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Settings, Save, Loader2 } from "lucide-react";

const NAVY = "#071A2D";
const EMERALD = "#064E3B";
const GOLD = "#C9A45C";

/**
 * BookingSettings (concierge-controlled flexible payment + cancellation policy).
 * Lives inside ProviderDashboard.
 */
export default function BookingSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/providers/booking-settings");
      setSettings(data);
    } catch { setSettings({ deposit_enabled: false, deposit_percent: 50, balance_due_days_before: 1, cancellation_policy: "moderate", free_cancellation_hours: 48 }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const { data } = await api.post("/providers/booking-settings", settings);
      setSettings(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } finally { setSaving(false); }
  };

  if (!settings) return null;

  const set = (patch) => setSettings({ ...settings, ...patch });

  return (
    <section data-testid="booking-settings" className="mt-10 rounded-3xl card-luxury-elevated p-6 sm:p-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: `${EMERALD}10`, color: EMERALD }}>
            <Settings size={20}/>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: EMERALD }}>Booking Settings</div>
            <h2 className="font-serif text-2xl mt-1" style={{ color: NAVY }}>Flexible payment & cancellation.</h2>
            <p className="text-sm mt-1" style={{ color: "rgba(7,26,45,0.6)" }}>
              Customers see these terms clearly before they pay.
            </p>
          </div>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="btn-coral inline-flex items-center gap-2 text-sm"
          data-testid="booking-settings-save"
        >
          {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
          {saved ? "Saved" : "Save"}
        </button>
      </div>

      {/* Down payment */}
      <div className="mt-7">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold mb-3" style={{ color: GOLD }}>Down payment</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <RadioCard
            checked={!settings.deposit_enabled}
            onClick={() => set({ deposit_enabled: false })}
            title="Full payment upfront"
            sub="Customer pays the full amount at checkout."
            testid="booking-deposit-full"
          />
          <RadioCard
            checked={!!settings.deposit_enabled}
            onClick={() => set({ deposit_enabled: true })}
            title="Deposit + balance"
            sub="Customer pays a deposit now, balance later."
            testid="booking-deposit-split"
          />
        </div>

        {settings.deposit_enabled && (
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <Field label="Deposit percent">
              <select
                value={settings.deposit_percent}
                onChange={(e) => set({ deposit_percent: +e.target.value })}
                className="kbd-input"
                data-testid="booking-deposit-percent"
              >
                {[10,15,20,25,30,40,50,60,75].map(p => <option key={p} value={p}>{p}%</option>)}
              </select>
            </Field>
            <Field label="Balance due (days before event)">
              <select
                value={settings.balance_due_days_before}
                onChange={(e) => set({ balance_due_days_before: +e.target.value })}
                className="kbd-input"
                data-testid="booking-balance-days"
              >
                <option value={0}>Day of event</option>
                <option value={1}>1 day before</option>
                <option value={3}>3 days before</option>
                <option value={7}>7 days before</option>
              </select>
            </Field>
          </div>
        )}
      </div>

      {/* Cancellation policy */}
      <div className="mt-8">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold mb-3" style={{ color: GOLD }}>Cancellation policy</div>
        <div className="grid sm:grid-cols-3 gap-3">
          {[
            { key: "flexible", title: "Flexible",  sub: "Free cancellation until your selected timeframe." },
            { key: "moderate", title: "Moderate",  sub: "Partial refund within rules. Most popular." },
            { key: "strict",   title: "Strict",    sub: "Limited cancellation window. Non-refundable after." },
          ].map((p) => (
            <RadioCard
              key={p.key}
              checked={settings.cancellation_policy === p.key}
              onClick={() => set({ cancellation_policy: p.key })}
              title={p.title}
              sub={p.sub}
              testid={`booking-cancel-${p.key}`}
            />
          ))}
        </div>

        <div className="mt-4">
          <Field label="Free cancellation up to">
            <select
              value={settings.free_cancellation_hours}
              onChange={(e) => set({ free_cancellation_hours: +e.target.value })}
              className="kbd-input"
              data-testid="booking-free-cancel-hours"
            >
              <option value={24}>24 hours before</option>
              <option value={48}>48 hours before</option>
              <option value={168}>7 days before</option>
              <option value={336}>14 days before</option>
            </select>
          </Field>
        </div>
      </div>

      <div className="mt-6 text-xs" style={{ color: "rgba(7,26,45,0.5)" }}>
        Customers will always see your <b>TREL Certified</b> badge, deposit terms, and cancellation policy on the booking page.
      </div>
    </section>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] uppercase tracking-wider font-semibold mb-1" style={{ color: "rgba(7,26,45,0.55)" }}>{label}</div>
    {children}
  </label>
);

function RadioCard({ checked, onClick, title, sub, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="text-left rounded-2xl p-4 border transition-all"
      style={{
        background: checked ? `${EMERALD}08` : "#fff",
        borderColor: checked ? EMERALD : "rgba(7,26,45,0.10)",
        boxShadow: checked ? `0 0 0 1px ${EMERALD} inset` : "none",
      }}
    >
      <div className="font-medium" style={{ color: NAVY }}>{title}</div>
      <div className="text-xs mt-1" style={{ color: "rgba(7,26,45,0.6)" }}>{sub}</div>
    </button>
  );
}
